import { TMultiOptionTypeMaterialUI, TOptionType, TOptionTypeDB, TOptionTypeUi } from './general.types';
import { EAnimalTypes } from './animals.types';
import { TCustomPetFee } from './petFee.types';
import { EPooCompaniesDB } from './residence.types';
import { TNotificationKey, TPMPermissionsDB, TUserPermissionsUI } from './permissions.types';
import {
  EAnimalInfoConfig,
  TCustomRequiredDocumentsUI,
  TPropertyConfigSetGetDB,
  TPropertyConfigSetGetUI,
  TRequiredDocumentsDB,
  TRequiredDocumentsUI,
} from './propertyConfigSet.types';

/**
 * `address1` is optional: it is not recorded on every property, and it is absent
 * from any response cached before PP-10045 added it.
 */
export type TPropertyDB = TOptionTypeDB & { address1?: string | null };

export type TPropertyUI = TMultiOptionTypeMaterialUI & { address1?: string | null };

export type TUnitDB = {
  id: string;
  number: string;
};

export type TUnitUI = TMultiOptionTypeMaterialUI;

export type TPropertyTypeUI = {
  title: string;
  keyWord: string;
};
export type TPropertyTypeDB = {
  key: string;
  displayName: string;
};

export enum EPropertiesTemplateTypeDB {
  'OPP' = 'OPP',
}

export enum EPropertiesTemplateTypeUI {
  'OPP' = 'OPP',
}

export type TPropertiesUploadDB = {
  type: EPropertiesTemplateTypeDB;
  file: File[];
};

export type TPropertiesUploadUI = {
  type: EPropertiesTemplateTypeUI;
  file: File[];
  shouldDeleteNotMatchedProperties?: string;
  animalRequirementsSetId: TOptionTypeUi;
};

export type TNotMatchedPropertyUI = {
  activeApplicantTenantResidenciesCount: number;
  numberOfActiveAnimals: number;
  currentTenantResidenciesCount: number;
  name: string;
  propertyId: string;
};
export type TNotMatchedPropertyDB = {
  activeApplicantTenantResidenciesCount: number;
  numberOfActiveAnimals: number;
  currentTenantResidenciesCount: number;
  name: string;
  propertyId: string;
};

export type TPropertiesCSVUploadResultUI = {
  skippedProperties: Blob | null;
  skippedPropertiesCount: number;
  removedPropertiesCount: number;
  addedPropertiesCount: number;
  matchedPropertiesCount: number;
  totalPropertiesCount: number;
  createdAccessCodesCount: number;
  matchedAccessCodesCount: number;
  notMatchedProperties: TNotMatchedPropertyUI[];
};

export type TPropertiesCSVUploadResultDB = {
  skippedProperties: string;
  skippedPropertiesCount: number;
  removedPropertiesCount: number;
  addedPropertiesCount: number;
  matchedPropertiesCount: number;
  totalPropertiesCount: number;
  createdAccessCodes: number;
  matchedAccessCodes: number;
  notMatchedProperties: TNotMatchedPropertyDB[];
};

export enum EPropertySourceDB {
  SYSTEM = 'SYSTEM',
  RESMAN = 'RESMAN',
}
export enum EPropertySourceUI {
  SYSTEM = 'System Created',
  RESMAN = 'Imp. from ResMan',
}
export enum EPropertyStatusDB {
  ACTIVE = 'Active',
  PENDING = 'Invited',
}
export enum EPropertyStatusUI {
  PENDING = 'Pending',
  ACTIVE = 'Active',
}
export type TPropertyStatisticsDB = {
  esaCounts: number;
  petsCounts: number;
  residentsCounts: number;
  saCounts: number;
  unitsCountWithAnimal: number;
};
export type TPropertyStatisticsUI = {
  esaCounts: number;
  petsCounts: number;
  residentsCounts: number;
  saCounts: number;
  unitsCountWithAnimal: number;
};

export type TPaymentSettings = {
  petDeposit: number;
  petFee: TCustomPetFee | null;
  petMonthlyFee: TCustomPetFee | null;
  unAuthPetFee: number;
};
// PP-10057: which animal types OPP manages at the property. Only the not-managed types carry a
// message, and a property with all three managed is the standard service (section hidden from PMs).
export type TAnimalTypeManagementDB = {
  notManagedTypes: Partial<Record<EAnimalTypes, string>>;
};

export type TAnimalTypeManagementUI = {
  notManagedTypes: Partial<Record<EAnimalTypes, string>>;
};

export type TChangeAnimalTypeManagementUI = TAnimalTypeManagementUI & {
  propertyId: string;
};

export type TPropertiesTypeDB = {
  id: string;
  name: string;
  photoUrl: string | null;
  unitCount: number;
  type: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  source: EPropertySourceDB;
  status: string;
  unitNumbers: string[];
  defaultPolicy: {
    id: string;
    name: string;
    petsAllowed: boolean;
  };
  userId: string;
  resourceId: string;
  propertyInternalId: number;
  isOnboarded: boolean;
  propertyStatistics: TPropertyStatisticsDB;
  lastSyncDate: string | null;
  lastFileExportDate: string | null;
  isScreeningEnabled: boolean;
  notifyExistingApplicants: boolean;
  notifyExistingFutureResidents: boolean;
  canEditScreenExistingTenantsBeforeOnboarding: boolean;
  paymentSettings: TPaymentSettings;
  animalVerificationConfiguration: TPropertyConfigSetGetDB;
  skipAutoScreeningForResidentsAlreadyInRenewalPeriod: boolean;
  autoScreeningDaysConfigured: boolean;
  screeningConfiguration: EScreeningConfiguration;
  dnaSettings?: TPropertyDnaSettingDB;
  animalTypeManagement?: TAnimalTypeManagementDB;
};
export type TPropertyDnaCompanyDB = {
  companyOption: EPooCompaniesDB | 'OTHER';
  companyCustomName: string | null;
};
export type TPropertyDnaSettingDB = {
  isEnabled: boolean;
  company: TPropertyDnaCompanyDB | null;
};
export type TPropertyDnaTestingUI = {
  isEnabled: boolean;
  company: EPooCompaniesDB | null;
};
export type TUpdatePropertyDnaTestingUI = TPropertyDnaTestingUI & {
  propertyId: string;
};
export type TPropertiesTypeUI = {
  id: string;
  name: string;
  photoUrl?: string | null;
  unitCount: number;
  type: string;
  address1: string;
  address2?: string;
  city: string;
  state: string;
  zip: string;
  source: EPropertySourceUI;
  status: string;
  unitNumbers: string[];
  defaultPolicy: {
    id: string;
    policyName: string;
  };
  petsAllowed: boolean;
  userId: string;
  resourceId: string;
  propertyInternalId: number;
  isOnboarded: boolean;
  propertyStatistics: TPropertyStatisticsUI;
  lastSyncDate: string | null;
  lastFileExportDate: string | null;
  isScreeningEnabled: boolean;
  notifyExistingApplicants: boolean;
  notifyExistingFutureResidents: boolean;
  canEditScreenExistingTenantsBeforeOnboarding: boolean;
  paymentSettings: TPaymentSettings;
  animalRequirementsSetDTO: TPropertyConfigSetGetUI;
  skipAutoScreeningForResidentsAlreadyInRenewalPeriod: boolean;
  autoScreeningDaysConfigured: boolean;
  screeningConfiguration: EScreeningConfiguration;
  dnaTesting: TPropertyDnaTestingUI;
  animalTypeManagement: TAnimalTypeManagementUI;
};

export type TCreatePropertyDB = {
  name: string;
  address1: string;
  address2?: string;
  city: string;
  zip: string;
  state: string;
  status: string;
  unitCount: number;
  unitNumbers: string[];
  type?: string;
  userId: string;
  defaultPolicyId?: string;
  photoUrl?: string;
  screenExistingTenants?: boolean;
  animalVerificationConfigurationId?: string;
  accessCodeName?: string;
  isCustomPropertyAllowed?: boolean;
  accessCodesToAssign?: string[];
};
export type TCreatePropertyUI = {
  name: string;
  address1: string;
  address2?: string;
  city: string;
  zip: string;
  state: string;
  status: string;
  unitCount: number;
  unitNumbers: string[];
  type?: string;
  userId: string;
  defaultPolicyId?: string;
  photoUrl?: string;
  screenExistingTenants?: boolean;
  animalRequirementsSetId?: string;
  accessCodeName?: string;
  isCustomPropertyAllowed?: boolean;
  accessCodeIds?: string[];
};

export type TUpdatePoilicyDB = {
  policyId: string;
  propertyId: string;
};
export type TUpdatePoilicyUI = {
  policyId: string;
  propertyId: string;
  shouldCopyChargeSettings: boolean;
};

export type TPropertiesDB = {
  id: string;
  name: string;
  photoUrl?: string | null;
  unitCount: number;
  type: string;
  address1: string;
  address2?: string;
  city: string;
  state: string;
  stateCode: string;
  zip: string;
  source: EPropertySourceDB;
  petsAllowed: boolean;

  defaultPolicy: {
    id: string;
    policyName: string;
  };
  status: EPropertyStatusDB;
};

export type TPropertiesUI = {
  id: string;
  name: string;
  photoUrl?: string | null;
  unitCount: number;
  type: string;
  address1: string;
  address2?: string;
  city: string;
  state: string;
  stateCode: string;
  zip: string;
  source: EPropertySourceUI;
  petsAllowed: boolean;
  defaultPolicy: {
    id: string;

    policyName: string;
  };
  status: EPropertyStatusUI;
};

export type TPropertyManagersTableDB = {
  allowedDelete: boolean;
  allowedReInvite: boolean;
  email: string;
  id: string;
  name: string;
  phone: string;
  position: string;
  profilePicture: string | null;
  propertiesCount: number;
  status: EPropertyStatusDB;
  permissions?: TPMPermissionsDB;
  notificationOptions?: string[];
  primaryContact?: boolean;
  animalUpdateConfig?: TAnimalUpdateConfig | null;
};
export type TPropertyManagersTableUI = {
  allowedDelete: boolean;
  allowedReInvite: boolean;
  email: string;
  id: string;
  name: string;
  phone: string;
  position: string;
  profilePicture: string | null;
  propertiesCount: number;
  status: EPropertyStatusUI;
  permissions?: TUserPermissionsUI;
  assignedNotifications?: TNotificationKey[];
  animalUpdateConfig?: TAnimalUpdateConfig | null;
};

export type TInvitePropertyMangerDB = {
  email: string;
  name: string;
  phone?: string;
  position: TOptionType;
  properties?: string[];
  accessToFutureProperties: boolean;
  permissionsUIType: string;
  permissionsId?: string;
  permissions?: string[];
  notificationOptions?: string[];
  isPrimaryContact: boolean;
  animalUpdateConfig?: TAnimalUpdateConfig | null;
};

export type TInvitePropertyManagerUI = {
  email: string;
  name: string;
  phone?: string;
  position: TOptionTypeUi;
  properties: TOptionTypeUi[];
  accessToFutureProperties: boolean;
};

export type TPropertyManagerPropertiesDB = {
  address1: string;
  id: string;
  type: string;
  unitCount: number;
  name: string;
  notificationEnabled: boolean;
  readOnlyAccess: boolean;
};

export type TPropertyManagerPropertiesUI = {
  address1: string;
  id: string;
  type: string;
  unitCount: number;
  name: string;
  notificationEnabled: boolean;
};

export type TAddUnitsDB = {
  propertyId: string;
  unitNumbers: string[];
};
export type TAddUnitsUI = {
  propertyId: string;
  unitNumbers: string[];
};

/** Mirrors the backend `EditOperation` enum carried on every `UpdatablePropertyDto`. */
export enum EEditOperation {
  UPDATE = 'UPDATE',
  REMOVE = 'REMOVE',
  ADD = 'ADD',
  SET = 'SET',
}

export type TPropertyManagerDetailsValueDB = {
  value?: string;
  allowedOperations: EEditOperation[];
};
export type TPropertyManagerDetailsValueUI = {
  value?: string;
  allowedOperations: EEditOperation[];
};
export type TPropertyManagerDetailsBooleanValueDB = {
  value?: boolean;
  allowedOperations: EEditOperation[];
};
export type TPropertyManagerDetailsBooleanValueUI = {
  value?: boolean;
  allowedOperations: EEditOperation[];
};

export enum ENotificationTypesDB {
  ANIMAL_APPROVALS_AND_DENIALS = 'ANIMAL_APPROVALS_AND_DENIALS',
  RESIDENT_DOES_NOT_HAVE_ANIMALS = 'RESIDENT_DOES_NOT_HAVE_ANIMALS',
  ANIMAL_ACTIVITY_SUMMARY = 'ANIMAL_ACTIVITY_SUMMARY',
}
export enum ENotificationTypesUI {
  ANIMAL_APPROVALS_AND_DENIALS = 'Animal Approvals and Denials',
  RESIDENT_DOES_NOT_HAVE_ANIMALS = 'Resident Does Not Have Animals',
  ANIMAL_ACTIVITY_SUMMARY = 'Animal Activity Summary',
}

export type TPropertyManagerDetailsDB = {
  email: TPropertyManagerDetailsValueDB;
  id: string;
  name: TPropertyManagerDetailsValueDB;
  phone: TPropertyManagerDetailsValueDB;
  position: TPropertyManagerDetailsValueDB;
  propertyManagementId: string;
  propertyManagementName: TPropertyManagerDetailsValueDB;
  hasAccessToFutureProperties: TPropertyManagerDetailsBooleanValueDB;
  notificationsEnabledForFutureProperties: TPropertyManagerDetailsBooleanValueDB;
  notifications: ENotificationTypesDB[];
  primaryContact: TPropertyManagerDetailsBooleanValueDB;
  // hasAccessToBillingMethod: boolean; // Deprecated
  // readOnlyAccess?: boolean; // Deprecated
  permissions?: TPMPermissionsDB;
  provisionedByScim?: boolean;
};
export type TPropertyManagerDetailsUI = {
  notifications: ENotificationTypesDB[];
  email: TPropertyManagerDetailsValueUI;
  id: string;
  name: TPropertyManagerDetailsValueUI;
  phone: TPropertyManagerDetailsValueUI;
  position: TPropertyManagerDetailsValueUI;
  propertyManagementId: string;
  propertyManagementName: TPropertyManagerDetailsValueUI;
  hasAccessToFutureProperties: TPropertyManagerDetailsBooleanValueUI;
  notificationsEnabledForFutureProperties: TPropertyManagerDetailsBooleanValueUI;
  primaryContact: TPropertyManagerDetailsBooleanValueUI;
  // hasAccessToBillingMethod: boolean; // Deprecated
  // readOnlyAccess?: boolean; // Deprecated
  permissions?: TUserPermissionsUI;
  provisionedByScim?: boolean;
};

export type TAssignedPropertiesDB = {
  id: string;
  name: string;
  notificationEnabled: boolean;
};
export type TAssignedPropertiesUI = {
  value: string;
  label: string;
  notificationEnabled: boolean;
};

export type TUnassignedPropertiesDB = {
  id: string;
  name: string;
};
export type TUnassignedPropertiesUI = {
  value: string;
  label: string;
};

export type TAssignPropertyDB = {
  properties: string[];
  propertyManagerId: string;
};
export type TAssignPropertyUI = {
  properties: TOptionTypeUi[];
  propertyManagerId: string;
};
export type TAssignTeamMembersDB = {
  propertyId: string;
  teamMemberIds: string[];
};
export type TAssignTeamMembersUI = {
  propertyId: string;
  teamMemberIds: TOptionTypeUi[];
};

export type TPetChargeStatusesUploadUI = {
  propertyId: string;
  type: TOptionTypeUi;
  files: File[];
};
export type TPetChargeStatusesUploadDB = FormData;

export type TPreopertyDeletePreviewDB = {
  moveOutResidencyCount: number;
  moveOutAnimalCount: number;
  retractedTenantApplicationCount: number;
  retractedAnimalApplicationCount: number;
};

export type TPreopertyDeletePreviewUI = {
  moveOutResidencyCount: number;
  moveOutAnimalCount: number;
  retractedTenantApplicationCount: number;
  retractedAnimalApplicationCount: number;
};

export type TPropertyUploadedFilesDB = {
  id: string;
  fileUrl: string;
  fileName: string;
  type: EUploadedFileTypeDB;
  exportTime: string;
  uploadTime: string;
  uploaderId: string;
  uploaderName: string;
};

export type TPropertyUploadedFilesUI = {
  id: string;
  fileUrl: string;
  fileName: string;
  type: EUploadedFileTypeUI;
  exportTime: string;
  uploadTime: string;
  uploaderId: string;
  uploaderName: string;
};

export enum EUploadedFileTypeUI {
  PET_CHARGES = 'Pet Charges',
  APPLICANTS = 'Applicants',
  RESIDENTS = 'Residents',
}
export enum EUploadedFileTypeDB {
  PET_CHARGES = 'PET_CHARGES',
  APPLICANTS = 'APPLICANTS',
  RESIDENTS = 'RESIDENTS',
}

export type TResidentsScreeningActionUI = {
  propertyId: string;
  needToScreen: boolean;
  notifyExistingApplicants?: boolean;
  notifyExistingFutureResidents?: boolean;
};
export type TResidentsScreeningActionDB = {
  propertyId: string;
  needToScreen: boolean;
  notifyExistingApplicants?: boolean;
  notifyExistingFutureResidents?: boolean;
};

export enum EScreeningConfiguration {
  ALL_RESIDENTS = 'ALL_RESIDENTS',
  ALL_RESIDENTS_WITH_ANIMALS = 'ALL_RESIDENTS_WITH_ANIMALS',
  RESIDENTS_WITH_ASSISTANCE_ANIMALS = 'RESIDENTS_WITH_ASSISTANCE_ANIMALS',
  RESIDENTS_WITH_PETS = 'RESIDENTS_WITH_PETS',
}
export const screeningConfigurationLabels: Record<EScreeningConfiguration, string> = {
  [EScreeningConfiguration.ALL_RESIDENTS]: 'All Residents',
  [EScreeningConfiguration.ALL_RESIDENTS_WITH_ANIMALS]: 'Residents with Animals',
  [EScreeningConfiguration.RESIDENTS_WITH_ASSISTANCE_ANIMALS]: 'Residents with Assistance Animals',
  [EScreeningConfiguration.RESIDENTS_WITH_PETS]: 'Residents with Pets',
};
export type TScreeningConfigurationUI = {
  propertyId: string;
  screeningConfiguration: EScreeningConfiguration;
};
export type TScreeningConfigurationDB = {
  propertyId: string;
  screeningConfiguration: EScreeningConfiguration;
};
export type TPropertyMetadataDB = {
  name: string;
  id: string;
  isYardi: boolean;
};
export type TPropertyMetadataUI = {
  name: string;
  resourceId: string;
  isYardi: boolean;
};
export type TCreateNotificationSetDB = {
  name: string;
  propertyManagerId: string;
};
export type TCreateNotificationSetUI = {
  name: string;
  propertyManagerId: string;
};

export enum EScheduleType {
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
  CUSTOM = 'CUSTOM',
}

export enum EDayOfWeek {
  MONDAY = 'MONDAY',
  TUESDAY = 'TUESDAY',
  WEDNESDAY = 'WEDNESDAY',
  THURSDAY = 'THURSDAY',
  FRIDAY = 'FRIDAY',
  SATURDAY = 'SATURDAY',
  SUNDAY = 'SUNDAY',
}

export type TAnimalUpdateConfig = {
  scheduleType: EScheduleType;
  weeklyDayOfWeek: EDayOfWeek | null;
  customIntervalDays: number | null;
  customStartDate: string | null;
  verifiedPetsEnabled: boolean;
  missingChargesEnabled: boolean;
  recentlyApprovedEnabled: boolean;
  recentlyNotApprovedEnabled: boolean;
  recentlyMovedOutEnabled: boolean;
  pendingMissingReqsEnabled: boolean;
  movedInWithoutApprovalEnabled: boolean;
};

export type TNotificationSettingsDB = {
  enabledNotificationTypes: string[];
  areFuturePropertiesIncluded: boolean;
  id: string;
  isPrimaryContact: boolean;
  name: string;
  properties: { name: string; propertyId: string }[];
  propertyManagerId: string;
  animalUpdateConfig?: TAnimalUpdateConfig | null;
  isReportingEnabled: boolean;
};
export type TNotificationSettingsUI = {
  enabledNotificationTypes: ENotificationTypesDB[];
  areFuturePropertiesIncluded: boolean;
  id: string;
  isPrimaryContact: boolean;
  name: string;
  properties: { name: string; propertyId: string }[];
  propertyManagerId: string;
  animalUpdateConfig?: TAnimalUpdateConfig | null;
  isReportingEnabled: boolean;
};

export type TNotificationSetUpdateDB = {
  notificationSettingsId: string;
  areFuturePropertiesIncluded: boolean;
  name: string;
  propertyIds: string[];
  isPrimaryContact: boolean;
  notificationOptions: ENotificationTypesDB[];
  propertyManagerId: string;
  animalUpdateConfig?: TAnimalUpdateConfig | null;
};

export type TNotificationSetUpdateUI = {
  notificationSettingsId: string;
  areFuturePropertiesIncluded: boolean;
  name: string;
  propertyIds: string[];
  isPrimaryContact: boolean;
  notificationOptions: ENotificationTypesDB[];
  propertyManagerId: string;
  animalUpdateConfig?: TAnimalUpdateConfig | null;
};

export type TTeamMember = {
  email: string;
  hasAccessToCurrentProperty: boolean;
  id: string;
  name: string;
  phone?: string;
  position: string;
  profilePicture?: string;
  status: string;
};

export type TPropertyPaymentStatistics = {
  paid: number;
  unPaid: number;
  paidPerUnitRatio: number;
  isChargeEnabled: boolean;
};

export type TPropertiesConfigEditTypeUI = {
  propertyId: string;
  name: string;
  criteria: EAnimalInfoConfig[];
  hiddenCriteria: EAnimalInfoConfig[];
  isPetAllowed: 'no' | 'yes';
  maxPets?: number;
  maxPetWeight?: number;
  restrictedBreeds?: TOptionTypeUi[];
  species?: TOptionTypeUi[];
  requiredDocuments: TRequiredDocumentsUI;
  customRequiredDocuments: TCustomRequiredDocumentsUI[];
  animalVerificationConfigurationId: string;
  newConfigurationSetName: string | null;
  reVerifyKnownAssistanceAnimals?: boolean;
  isCourseRequired?: boolean;
  hideDocumentsSection?: boolean;
};
export type TPropertiesConfigEditTypeDB = {
  propertyId: string;
  name: string;
  criteria: EAnimalInfoConfig[];
  hiddenCriteria: EAnimalInfoConfig[];
  newConfigurationSetName: string | null;
  noPetPolicy: boolean;
  petRestrictions?: {
    maxNumOfPetsAllowed?: number;
    maxPetWeight?: number;
    restrictedBreeds?: string[];
    allowedSpecies?: string[];
  };
  requiredVetRecords: TRequiredDocumentsDB[];
  animalVerificationConfigurationId: string;
  reVerifyKnownAssistanceAnimals: boolean;
  isAnimalCurriculumRequired: boolean;
  hideDocumentsSection: boolean;
};

export type TTeamMembersUploadUI = {
  file: File[];
};

export type TTeamMembersCSVUploadResultUI = {
  totalRowsProcessed: number;
  usersToCreate: number;
  usersToSkip: number;
  usersToDeactivate: number;
  usersToUpdate: number;
};

export type TTeamMembersCSVUploadResultDB = {
  totalRowsProcessed: number;
  usersToCreate: number;
  usersToSkip: number;
  usersToDeactivate: number;
  usersToUpdate: number;
};
